import * as React from "react";
import { ChevronRight, Folder, FolderOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";

interface TreeNode {
  id: number;
  name: string;
  sort?: number | null;
  children?: TreeNode[];
  [key: string]: unknown;
}

interface TreeViewProps {
  nodes: TreeNode[];
  renderNode?: (node: TreeNode) => React.ReactNode;
  className?: string;
  defaultExpanded?: boolean;
  onNodeClick?: (node: TreeNode) => void;
  expanded?: boolean;
}

function TreeNodeComponent({
  node,
  renderNode,
  defaultExpanded = false,
  onNodeClick,
  level = 0,
  forceExpanded,
}: {
  node: TreeNode;
  renderNode?: (node: TreeNode) => React.ReactNode;
  defaultExpanded?: boolean;
  onNodeClick?: (node: TreeNode) => void;
  level?: number;
  forceExpanded?: boolean;
}) {
  const initialExpanded = forceExpanded !== undefined ? forceExpanded : defaultExpanded;
  const [isOpen, setIsOpen] = React.useState(initialExpanded);
  const hasChildren = node.children && node.children.length > 0;

  // Sync with forceExpanded prop
  React.useEffect(() => {
    if (forceExpanded !== undefined) {
      setIsOpen(forceExpanded);
    }
  }, [forceExpanded]);

  const handleClick = () => {
    if (onNodeClick) {
      onNodeClick(node);
    }
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on action buttons or links
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("a") ||
      target.closest('[role="button"]') ||
      target.closest(".opacity-0")
    ) {
      return;
    }
    if (hasChildren) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="select-none">
      <div
        className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent"
        style={{ paddingLeft: `${level * 1.5}rem` }}
      >
        {hasChildren ? (
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div
              className="flex items-center gap-2 flex-1 w-full cursor-pointer"
              onClick={handleRowClick}
            >
              <ChevronRight
                className={cn(
                  "h-3 w-3 transition-transform duration-200 shrink-0",
                  isOpen && "rotate-90"
                )}
              />
              {isOpen ? (
                <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                {renderNode ? renderNode(node) : <span onClick={handleClick}>{node.name}</span>}
              </div>
            </div>
            <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
              <div className="pt-1">
                {node.children?.map((child) => (
                  <TreeNodeComponent
                    key={child.id}
                    node={child}
                    renderNode={renderNode}
                    defaultExpanded={defaultExpanded}
                    onNodeClick={onNodeClick}
                    level={level + 1}
                    forceExpanded={forceExpanded}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <div
            className="flex items-center gap-2 flex-1 w-full cursor-pointer"
            onClick={handleRowClick}
          >
            <div className="size-4 shrink-0" />
            <span className="text-muted-foreground shrink-0">└</span>
            <div className="flex-1" onClick={(e) => e.stopPropagation()}>
              {renderNode ? renderNode(node) : <span onClick={handleClick}>{node.name}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function TreeView({
  nodes,
  renderNode,
  className,
  defaultExpanded = false,
  onNodeClick,
  expanded,
}: TreeViewProps) {
  return (
    <div className={cn("rounded-lg border bg-card p-4", className)}>
      {nodes.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">لا توجد مناطق مسجلة</div>
      ) : (
        <div className="space-y-1">
          {nodes.map((node) => (
            <TreeNodeComponent
              key={node.id}
              node={node}
              renderNode={renderNode}
              defaultExpanded={expanded !== undefined ? expanded : defaultExpanded}
              onNodeClick={onNodeClick}
              forceExpanded={expanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}
